import * as React from 'react';
import { IconMapHighway16, IconMapTrain16 } from '@cian/ui-kit-design-tokens/icons';
import { UIText1 } from '@cian/ui-kit/typography';

import { GeoTravelTime } from '../GeoInformation/components';
import { UndergroundIcon } from '../GeoInformation/components/Underground/components/UndergroundIcon';
import { ETransportType, ETravelType } from '../../types/geo';
import type { TClosestGeo } from '../../types/recommendation';
import * as styles from './ClosestGeo.css';

type TClosestGeoProps = {
  closestGeo: TClosestGeo;
  regionId: number;
};

const SPB_REGION_ID = 2;

const TRAVEL_TYPE_TO_TRANSPORT_TYPE_MAP: Partial<Record<ETravelType, ETransportType>> = {
  [ETravelType.ByCar]: ETransportType.Transport,
  [ETravelType.ByFoot]: ETransportType.Walk,
};

function normalizeTime(time?: number | string | null) {
  const value = Number(time);

  return Number.isFinite(value) && value > 0 ? value : null;
}

function getTransportType(closestGeo: TClosestGeo) {
  if (closestGeo.transportType) {
    return closestGeo.transportType;
  }

  return closestGeo.travelType ? TRAVEL_TYPE_TO_TRANSPORT_TYPE_MAP[closestGeo.travelType] : undefined;
}

function formatHighwayDistance(distance: TClosestGeo['distance'], regionId: number) {
  if (distance === null || distance === undefined || distance === '') {
    return null;
  }

  const rawDistance = String(distance).trim();

  if (!rawDistance) {
    return null;
  }

  const distanceWithUnit = rawDistance.includes('км') ? rawDistance : `${rawDistance} км`;
  const ringRoadName = regionId === SPB_REGION_ID ? 'КАД' : 'МКАД';

  return /(?:^|\s)(?:МКАДа?|КАДа?)(?:\s|$)/i.test(distanceWithUnit)
    ? distanceWithUnit
    : `${distanceWithUnit} от ${ringRoadName}`;
}

export const ClosestGeo: React.FC<TClosestGeoProps> = ({ closestGeo, regionId }) => {
  if (closestGeo.type === 'underground') {
    if (!closestGeo.name) {
      return null;
    }

    const travelTime = normalizeTime(closestGeo.time);
    const transportType = getTransportType(closestGeo);

    return (
      <div className={styles['container']}>
        {closestGeo.lineColor && (
          <UndergroundIcon className={styles['icon']} color={`#${closestGeo.lineColor}`} regionId={[regionId]} />
        )}
        <span className={styles['name']}>
          <UIText1 display="inline">{closestGeo.name}</UIText1>
        </span>
        {travelTime && transportType && (
          <span className={styles['travel-time']}>
            <GeoTravelTime time={travelTime} transportType={transportType} isRedesign={true} />
          </span>
        )}
      </div>
    );
  }

  if (closestGeo.type === 'railway') {
    const travelTime = normalizeTime(closestGeo.time);
    const transportType = getTransportType(closestGeo);

    if (!closestGeo.name && (!travelTime || !transportType)) {
      return null;
    }

    return (
      <div className={styles['container']}>
        <span className={styles['icon']}>
          <IconMapTrain16 display="block" color="icon-secondary-default" />
        </span>
        {closestGeo.name && (
          <span className={styles['name']}>
            <UIText1 display="inline">{closestGeo.name}</UIText1>
          </span>
        )}
        {travelTime && transportType && (
          <span className={styles['travel-time']}>
            <GeoTravelTime time={travelTime} transportType={transportType} isRedesign={true} />
          </span>
        )}
      </div>
    );
  }

  const highwayDistance = formatHighwayDistance(closestGeo.distance, regionId);

  if (!closestGeo.name && !highwayDistance) {
    return null;
  }

  return (
    <div className={styles['container']}>
      <span className={styles['icon']}>
        <IconMapHighway16 display="block" color="icon-secondary-default" />
      </span>
      {closestGeo.name && (
        <span className={styles['name']}>
          <UIText1 display="inline">{closestGeo.name}</UIText1>
        </span>
      )}
      {highwayDistance && (
        <span className={styles['highway-distance']}>
          <UIText1 display="inline" color="text-secondary-default">
            {closestGeo.name ? `, ${highwayDistance}` : highwayDistance}
          </UIText1>
        </span>
      )}
    </div>
  );
};
